import React, { useCallback, useEffect, useRef, useState } from "react"
import { ToastContainer, toast } from "react-toastify"
import { useDispatch } from "react-redux"
import { Modal } from "react-bootstrap"
import moment from "moment"
import { request } from "../../services/request"
import { ContactMessage, ContactMessageResponse } from "../../services/authinterface"
import Table, { TableAction } from "../../components/common/Table"
import PrimaryButton from "../../components/subComponents/PrimaryButton"
import { useDebounce } from "../../hooks/useDebounce"
import { changeloaderState } from "../../store/loaderSlice"
import { AppDispatch } from "../../store/store"
import { ViewIcon } from "../../icons/webSvg"
import { omitEmpty } from "../../utils/utils"

export const MESSAGE_PREVIEW_LENGTH = 80

export const truncateMessage = (message: string, length = MESSAGE_PREVIEW_LENGTH): string => {
  const text = (message || "").replace(/\s+/g, " ").trim()
  return text.length > length ? `${text.slice(0, length).trimEnd()}...` : text
}

const fullName = (row: ContactMessage) =>
  `${row.first_name || ""} ${row.last_name || ""}`.trim() || "-"

const formatDate = (date: string) => (date ? moment(date).format("MMM-DD-YYYY HH:mm") : "-")

const ContactMessages: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const [messages, setMessages] = useState<ContactMessage[]>([])
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalItems, setTotalItems] = useState(0)
  const [search, setSearch] = useState("")
  const debounceText = useDebounce(search, 400)
  const [sort, setSort] = useState<string | null>("DESC")
  const [sortCol, setSortCol] = useState<string | null>("createdAt")
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [selected, setSelected] = useState<ContactMessage | null>(null)
  const latestRequest = useRef(0)

  const fetchMessages = useCallback(async () => {
    // a slow response for an older search or page must not overwrite a newer one
    const requestId = ++latestRequest.current
    setIsLoading(true)
    setLoadError(false)
    try {
      dispatch(changeloaderState(true))
      const params = {
        page: page,
        limit: rowsPerPage,
        search: debounceText.trim(),
        sort: sort,
        sortCol: sortCol,
      }
      const response = await request<ContactMessageResponse>({
        url: "/contact/list",
        method: "POST",
        data: omitEmpty(params),
      })
      if (requestId !== latestRequest.current) return
      setMessages(response.data ?? [])
      setTotalItems(response.total ?? 0)
    } catch (error: any) {
      if (requestId !== latestRequest.current) return
      // 401 is handled by the api client (token refresh, then logout)
      if (error?.response?.status === 401) return
      setLoadError(true)
      setMessages([])
      setTotalItems(0)
      toast.error(
        error?.response?.status === 400
          ? error?.response?.data?.message || "Invalid request"
          : "Could not load contact messages. Please try again.",
      )
    } finally {
      if (requestId === latestRequest.current) setIsLoading(false)
      dispatch(changeloaderState(false))
    }
  }, [dispatch, page, rowsPerPage, debounceText, sort, sortCol])

  useEffect(() => {
    fetchMessages()
  }, [fetchMessages])

  useEffect(() => {
    setPage(1)
  }, [debounceText])

  const columns = [
    {
      name: "Name",
      value: (row: ContactMessage) => fullName(row),
      id: "first_name",
      sort: true,
      className: "long-text-devices",
    },
    {
      name: "Email",
      value: (row: ContactMessage) => row.email || "-",
      id: "email",
      sort: true,
      className: "long-text-devices",
    },
    {
      name: "Mobile",
      value: (row: ContactMessage) => row.mobile || "-",
      id: "mobile",
      sort: true,
    },
    {
      name: "Message",
      value: (row: ContactMessage) => truncateMessage(row.message),
      id: "message",
    },
    {
      name: "Submitted",
      value: (row: ContactMessage) => formatDate(row.createdAt),
      id: "createdAt",
      sort: true,
    },
  ]

  const action: TableAction[] = [
    {
      label: "",
      onClick: (row: ContactMessage) => setSelected(row),
      className: "table-btn-2",
      icon: <ViewIcon />,
    },
  ]

  const isSearching = debounceText.trim() !== ""

  return (
    <section className=' dashboard-section dashboard-item '>
      <ToastContainer position='top-right' autoClose={2000} theme='light' />
      <Modal className='comman-modal' show={!!selected} onHide={() => setSelected(null)} size='lg' centered>
        <Modal.Header closeButton className='comman-modal-header'>
          <Modal.Title className='comman-modal-title'>Contact Message</Modal.Title>
        </Modal.Header>
        <Modal.Body className='comman-modal-body'>
          {selected && (
            <div>
              <p>
                <strong>Name :</strong> {fullName(selected)}
              </p>
              <p>
                <strong>Email :</strong> {selected.email || "-"}
              </p>
              <p>
                <strong>Mobile :</strong> {selected.mobile || "-"}
              </p>
              <p>
                <strong>Submitted :</strong> {formatDate(selected.createdAt)}
              </p>
              <p className='mb-1'>
                <strong>Message :</strong>
              </p>
              {/* public input, rendered as text only */}
              <p
                data-testid='contact-message-body'
                style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: "50vh", overflowY: "auto" }}
              >
                {selected.message}
              </p>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <div className='container'>
        <div className='row'>
          <div className='col-12'>
            <div className='dashboard-table-title-section'>
              <h2>Contact Messages</h2>
            </div>
          </div>
          <div className='col-12 mt-4'>
            <div className='table-container-main'>
              <div className='table-details-section'></div>
              <Table
                rowData={messages}
                columns={columns}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                totalItems={totalItems}
                setSortColumn={setSortCol}
                setSortOrder={setSort}
                sortColumn={sortCol}
                sortOrder={sort}
                isSearch
                search={search}
                setSearch={setSearch}
                action={action}
                onRowClick={(row: ContactMessage) => setSelected(row)}
                pageSizeOptions={[10, 25, 50]}
              />
              {isLoading && messages.length === 0 && (
                <p className='text-center my-4'>Loading messages...</p>
              )}
              {!isLoading && loadError && (
                <div className='d-flex flex-column align-items-center my-4 gap-2'>
                  <p className='m-0'>Could not load contact messages.</p>
                  <PrimaryButton label='Retry' className='max-w-max-content' onClick={fetchMessages} />
                </div>
              )}
              {!isLoading && !loadError && messages.length === 0 && (
                <p className='text-center my-4'>
                  {isSearching ? `No messages match "${debounceText.trim()}".` : "No messages yet"}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default ContactMessages
